import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { businessTypeSchema, type BusinessType } from '@hero/schema';
import { SelectAuto, type SelectAutoOption } from '../Select';
import { FieldShell } from './shared/FieldShell';
import type { RhfFieldBaseProps } from './shared/types';

export type BusinessTypeFieldProps = RhfFieldBaseProps;

const BUSINESS_TYPE_KEYS = businessTypeSchema.options;

/**
 * `BusinessTypeField` — RHF-bound dropdown for
 * `Org.legal.businessType` (legal form of the entity, e.g.
 * LLC / مؤسسة فردية / مساهمة مبسطة).
 *
 * Optional everywhere — most existing rows pre-date the field.
 * Lenders use this to choose the right contract template;
 * analytics use it to slice the underwriting funnel by entity
 * shape.
 *
 * Options are pulled directly from `businessTypeSchema.options`
 * so the dropdown can never drift from the backend enum.
 * Translations live under `m-organization:entity_types.<key>`.
 */
export function BusinessTypeField(props: BusinessTypeFieldProps) {
  const { name, label, placeholder, helper, required, financing, disabled, className } =
    props;
  const { control } = useFormContext();
  const { t } = useTranslation(['m-organization']);
  const generatedId = useId();
  const inputId = `field-${name}-${generatedId}`;

  const options: SelectAutoOption[] = BUSINESS_TYPE_KEYS.map(key => ({
    value: key,
    label: t(`m-organization:entity_types.${key}`),
  }));

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          label={label ?? t('m-organization:form.entity_type.title')}
          helper={helper ?? t('m-organization:form.entity_type.helper')}
          required={required}
          financing={financing}
          error={fieldState.error?.message}
          htmlFor={inputId}
          className={className}>
          <SelectAuto
            value={(field.value as BusinessType | undefined) ?? undefined}
            options={options}
            onChange={field.onChange}
            placeholder={placeholder ?? t('m-organization:form.entity_type.placeholder')}
            disabled={disabled}
          />
        </FieldShell>
      )}
    />
  );
}
